import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Campaign } from '@/services/campaignService';
import { format } from 'date-fns';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

const CampaignEmailLogDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['campaign-email-logs', campaign.id],
    enabled: open,
    queryFn: async () => {
      const { data: emailLogs } = await supabase
        .from('email_logs')
        .select('*')
        .eq('campaign_id', campaign.id)
        .order('sent_at', { ascending: false });

      return emailLogs || [];
    },
  });

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'sent':
        return <Badge variant="default" className="bg-green-600">Sent</Badge>;
      case 'simulated':
        return <Badge className="bg-violet-600 text-white">Simulated</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Email Log — {campaign.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Loading logs...</p>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No email logs found for this campaign.</p>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log: any) => (
                    <React.Fragment key={log.id}>
                      <TableRow>
                        <TableCell className="text-sm">{log.to_email}</TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{log.provider_used || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.sent_at ? format(new Date(log.sent_at), 'MMM d, h:mm a') : '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                          >
                            {expandedRow === log.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedRow === log.id && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30">
                            <div className="p-3">
                              <p className="text-xs font-medium mb-1">Subject: {log.subject}</p>
                              <p className="text-xs text-muted-foreground mb-2">From: {log.from_email}</p>
                              {log.error_message && (
                                <p className="text-xs text-destructive mb-2">Error: {log.error_message}</p>
                              )}
                              <div
                                className="border rounded bg-background p-3 text-xs max-h-[200px] overflow-auto"
                                dangerouslySetInnerHTML={{ __html: log.body || '<em>No content</em>' }}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignEmailLogDialog;
